import React from 'react';
import { transcriptionService } from '../../services/api';
import { TranscriptionStats } from '../../types';

// What language each domain's calls are transcribed in — the settings half of
// the Transcription panel, separated from the usage half because one is a
// record of what happened and the other decides what happens next.
//
// The setting lives here rather than on Territories & Domains for the same
// reason the per-domain consent defaults live on the Consent page: a policy
// belongs with its subject matter. It also cannot be read without its
// consequences — the confidence spread one tab over is how you find out the
// language is wrong, and the model column only makes sense beside the
// explanation of why some languages have no telephony model at all.

const nf = new Intl.NumberFormat();

// The model, in the terms that matter when choosing one. These are Google's
// model names; what an operator needs to know is what each was trained on.
const MODEL_HINTS: Record<string, string> = {
  phone_call: 'Enhanced, trained on 8 kHz telephony — what this recorder captures',
  telephony: 'Trained on telephony audio; the newer model, no enhanced tier',
  default: 'General wideband model. Used where a language has no telephony model at all — expect lower confidence on call audio, however good the recording',
};

interface Props {
  stats: TranscriptionStats;
  // Saving re-reads the stats: the model a domain resolves to depends on the
  // language just chosen, so the row has to come back from the server rather
  // than be patched locally.
  onSaved: () => Promise<void> | void;
}

const TranscriptionLanguages: React.FC<Props> = ({ stats, onSaved }) => {
  const [saving, setSaving] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [note, setNote] = React.useState<string | null>(null);

  const save = async (territory: string, domain: string, language: string, model?: string) => {
    const key = `${territory}/${domain}`;
    setSaving(key);
    setError(null);
    setNote(null);
    try {
      const r = await transcriptionService.setDomainLanguage(territory, domain, language, model);
      // A model that the new language cannot use is dropped server-side. Say
      // so — a choice that silently disappears looks like a bug the next time
      // someone opens the page.
      if (r.model_cleared) {
        setNote(`${domain}: the model choice was cleared — ${r.effective} does not support it. `
          + `Now using ${r.model}.`);
      }
      await onSaved();
    } catch (e: any) {
      setError(e.response?.data?.error || `Could not save the language for ${domain}`);
    } finally {
      setSaving(null);
    }
  };

  return (
    <>
      <h3>Language by domain</h3>
      <p className="small">
        Recognition is told the language; it does not work it out. Google's
        automatic detection is documented for voice commands and searches, not
        for phone calls — and asked for the wrong language the engine does not
        fail, it returns the handful of words it can force-fit, at low
        confidence. So each domain says what its calls are in. It is a default,
        not a constraint: a single recording can be recognised in another
        language from the vCons page.
      </p>
      <p className="small">
        The model normally follows the language and only needs setting where
        the choice is a real one. <span className="monospace">phone_call</span> is
        trained on the 8&nbsp;kHz telephony this recorder captures;
        <span className="monospace"> telephony</span> is its newer counterpart.
        Neither exists for every language — Cantonese and Mandarin have no
        telephony model at all — and only the models a language actually
        supports are offered, because Google does not refuse one it lacks, it
        substitutes another and says nothing.
      </p>

      {error && <div className="error">{error}</div>}
      {note && <p className="small" style={{ opacity: 0.85 }}>{note}</p>}

      <div className="table-container">
        <table className="wide-table" style={{ maxWidth: '860px' }}>
          <thead>
            <tr>
              <th>Territory</th>
              <th>Domain</th>
              <th style={{ textAlign: 'right' }}>Calls</th>
              <th>Language</th>
              <th>Model</th>
            </tr>
          </thead>
          <tbody>
            {stats.domain_languages.map(d => {
              const key = `${d.territory}/${d.domain}`;
              const busy = saving === key;
              return (
                <tr key={key}>
                  <td className="small">
                    {d.territory || <span style={{ opacity: 0.5 }}>(unassigned)</span>}
                  </td>
                  <td className="monospace small">{d.domain}</td>
                  <td style={{ textAlign: 'right' }}>{nf.format(d.calls)}</td>
                  <td>
                    <select value={d.stt_language ?? ''} disabled={busy}
                            onChange={e => save(d.territory, d.domain, e.target.value)}>
                      <option value="">follows the default ({stats.default_language})</option>
                      {stats.languages.map(l => (
                        <option key={l.code} value={l.code}>{l.label} — {l.code}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {d.models.length === 1 ? (
                      // No choice to offer: this language has exactly one
                      // usable model. A one-option dropdown would imply
                      // otherwise.
                      <span className="monospace small" title={MODEL_HINTS[d.model]}>
                        {d.model}{d.model === 'default' ? ' ⚠' : ''}
                      </span>
                    ) : (
                      <select value={d.stt_model ?? ''} disabled={busy}
                              title={MODEL_HINTS[d.model]}
                              onChange={e => save(d.territory, d.domain,
                                                  d.stt_language ?? '', e.target.value)}>
                        <option value="">automatic ({d.models[0]})</option>
                        {d.models.map(m => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                    )}
                  </td>
                </tr>
              );
            })}
            {stats.domain_languages.length === 0 && (
              <tr><td colSpan={5}>No domains in scope.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="small" style={{ marginTop: '10px' }}>
        Changing a language changes nothing already transcribed — it is the
        assumption every future run on that domain makes. To correct calls
        already transcribed in the wrong language, use <strong>Redo</strong> on
        the vCons page; re-running replaces the transcript rather than adding a
        second one.
      </p>
    </>
  );
};

export default TranscriptionLanguages;
